import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { History, Mail, MessageSquare, Bell } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { NotificationLog } from '@/types/client'

interface ClientHistoryTabProps {
  logs?: NotificationLog[]
}

const typeIcons: Record<string, React.ReactNode> = {
  email: <Mail className="w-4 h-4" />,
  sms: <MessageSquare className="w-4 h-4" />,
  system: <Bell className="w-4 h-4" />,
}

const typeVariants: Record<string, 'mint' | 'gold' | 'brown'> = {
  email: 'gold',
  sms: 'mint',
  system: 'brown',
}

const typeLabels: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  system: 'Systeme',
}

export function ClientHistoryTab({ logs }: ClientHistoryTabProps) {
  if (!logs || logs.length === 0) {
    return (
      <EmptyState
        icon={<History className="w-12 h-12" />}
        title="Aucun historique"
        description="Aucune activite n'a ete enregistree pour ce client."
      />
    )
  }

  return (
    <Card padding="md">
      <div className="space-y-4">
        {logs.map((log, index) => (
          <div
            key={log.id}
            className={`flex gap-4 pb-4 ${
              index < logs.length - 1 ? 'border-b border-beige-200' : ''
            }`}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-beige-100 flex items-center justify-center text-brown-500">
              {typeIcons[log.type] || <Bell className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-brown-900">{log.titre}</span>
                <Badge variant={typeVariants[log.type]} size="sm">
                  {typeLabels[log.type]}
                </Badge>
              </div>
              <p className="text-sm text-brown-600 mb-1">{log.message}</p>
              <span className="text-xs text-brown-400">
                {formatDate(log.created_at, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
